/**
 * Renderer palette and metrics.
 *
 * Colours are written into the SVG as presentation attributes rather than
 * pulled from CSS, so that an exported SVG is self-contained and looks exactly
 * like the screen (invariant 7). They track the shell's custom properties in
 * styles.css.
 */
import type { Severity } from "../design/document";

export const THEME = {
  ink: "#16211f",
  muted: "#5c6b68",
  line: "#9aa8a5",
  surface: "#ffffff",
  grid: "#e3e8e7",
  accent: "#00728f",
  channel: "#2f5d73",
  frame: "#7a8b88",
  /**
   * The wash under one side of the external interfaces: a light cool green for
   * the consumable inside them, a pale grey-blue for what lies outside, so the
   * two never read as the same thing.
   */
  washInside: "#d6eee6",
  washOutside: "#dde6ee",
  /**
   * A script step's preview (D42): liquid in lines, by fluid, and what gas
   * regions are open to. Soft bands under the lines, so the drawing reads.
   */
  previewFluids: ["#3f8fd2", "#2fa38c", "#9a67cf", "#d08a3c", "#c9567a"],
  previewPressure: "#e8894a",
  previewVacuum: "#7a70d6",
  previewOpen: "#2f8f57",
  previewClosed: "#c0453a",
  /** A note's card: white, edged in pale blue, so it reads as a remark, not a component. */
  note: "#ffffff",
  noteEdge: "#a9cfe0",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  /** A port's status and other readings in a preview: figures line up. */
  monoFont: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: 11,
  labelFontSize: 10,
  strokeWidth: 1.5,
  edgeWidth: 2,
  portRadius: 3.5,
  badgeRadius: 6,
} as const;

/**
 * Severity is encoded by shape and mark as well as colour, so a badge is
 * distinguishable without colour vision.
 */
export const SEVERITY_STYLE: Record<Severity, { fill: string; mark: string; label: string }> = {
  // Muted rather than alarm colours: during schematic capture most of a design
  // fails some rule, and a findings badge is a note to come back to, not an
  // alarm. Severity still reads by shape and mark, not colour alone.
  error: { fill: "#b5655a", mark: "!", label: "error" },
  warning: { fill: "#a88532", mark: "!", label: "warning" },
  info: { fill: "#5f8f9c", mark: "i", label: "information" },
};
